import React from 'react';
import type { FormikProps } from 'formik';
import type { Contacts, IframeContacts } from '../../shared/types';
import { Flex, TextV2, ButtonV2 } from '@withjoy/joykit';
import { Wrapper, Footer } from '../components';
import { useAddressController } from './Address.controller';
import { Selector } from './components/Selector/Selector';
import { JoyLogoLoader } from '@shared/components/JoyLogoLoader';

interface AddressProps
  extends Readonly<{
    formik: FormikProps<Contacts> | FormikProps<IframeContacts>;
    isIframeVersion?: boolean;
    isMutationLoading?: boolean;
    hasMutationError?: boolean;
    // Optional caller-owned navigation for "back to Form", used when Address
    // is rendered outside a routing context (e.g. in a dialog).
    onNavigateBack?: () => void;
  }> {}

export const Address: React.FC<AddressProps> = ({ formik, isIframeVersion = false, isMutationLoading, hasMutationError, onNavigateBack }) => {
  const { isValidatingAddress, addressOption, setAddressOption, suggestedAddress, tAdmin, handleOnConfirmAddressClick, handleOnBackClick } = useAddressController(
    formik,
    isIframeVersion,
    onNavigateBack,
  );

  if (isValidatingAddress || isMutationLoading) return <JoyLogoLoader loaderKey="contact-collector-address" />;

  return (
    <Wrapper>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        paddingX={[isIframeVersion ? '40px' : '24px', null, 0]}
        width="100%"
      >
        <TextV2
          typographyVariant="hed6"
          marginTop={['24px', null, '4px']}
          textAlign="center"
        >
          {tAdmin.addressSuggestion()}
        </TextV2>
        <TextV2
          typographyVariant="body1"
          marginTop="32px"
          width="100%"
        >
          {tAdmin.addressSuggestionText1()}
        </TextV2>
        <TextV2
          typographyVariant="body1"
          marginTop="32px"
          width="100%"
        >
          {tAdmin.addressSuggestionText2()}
        </TextV2>
        <Selector
          formik={formik}
          suggestedAddress={suggestedAddress}
          addressOption={addressOption}
          setAddressOption={setAddressOption}
        />
        <ButtonV2
          intent="primary"
          shape="rounded"
          marginTop="32px"
          onClick={handleOnConfirmAddressClick}
          fullWidth
        >
          {tAdmin.confirmAddress()}
        </ButtonV2>
        <ButtonV2
          marginTop="16px"
          variant="ghost"
          color="mono14"
          onClick={handleOnBackClick}
          fullWidth
        >
          {tAdmin.back()}
        </ButtonV2>
        {hasMutationError && (
          <TextV2
            typographyVariant="body3"
            color="red"
            marginTop="16px"
          >
            {tAdmin.contactError()}
          </TextV2>
        )}
        {!isIframeVersion && <Footer />}
      </Flex>
    </Wrapper>
  );
};
